// v2 mushroom — a small family of toadstools: a tall parent with a conical
// skirted cap, a mid one beside it and a button sprout — the two bigger caps
// dotted with pale specks. Variants re-roll the cap colour (fly-agaric red /
// amber / chestnut) and turn the whole clump. Ported 1:1 from age-of-mcp's
// v2/mushroom.ts (the source uses no shared helpers — only the local `shroom` and
// `speck` builders, inlined here).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const CAP = ["#c0392b", "#d07a2a", "#8a5a36"];

function mushroomParts(v: number): Part[] {
  const cap = CAP[v % CAP.length]!;
  const yaw = v * 2.2 + 0.4;
  // rotate the clump layout per variant
  const rp = (x: number, z: number): [number, number] =>
    [x * Math.cos(yaw) - z * Math.sin(yaw), x * Math.sin(yaw) + z * Math.cos(yaw)];
  const parts: Part[] = [];
  // one toadstool: a pale tapering stem under a conical skirted cap;
  // returns the position and top of its cap for placing specks
  const shroom = (x: number, z: number, stemH: number, capR: number):
      { px: number; pz: number; base: number } => {
    const [px, pz] = rp(x, z);
    const capH = capR * 0.7;
    parts.push({ geometry: new THREE.CylinderGeometry(capR * 0.26, capR * 0.38, stemH, 5),
      color: "#efe3c4", position: [px, stemH / 2, pz] });
    parts.push({ geometry: new THREE.CylinderGeometry(capR * 0.22, capR, capH, 7),
      color: cap, position: [px, stemH + capH / 2 - 0.004, pz] });
    return { px, pz, base: stemH };
  };
  const big = shroom(0.012, 0.008, 0.046, 0.042);   // the parent
  const mid = shroom(-0.036, -0.018, 0.03, 0.03);   // leaning company
  shroom(0.028, -0.032, 0.015, 0.017);              // a button sprout
  // pale specks pressed into the two bigger caps
  const speck = (at: { px: number; pz: number; base: number },
      dx: number, dz: number, dy: number, r: number) => {
    parts.push({ geometry: new THREE.IcosahedronGeometry(r, 0),
      color: "#f6f1e2", position: [at.px + dx, at.base + dy, at.pz + dz] });
  };
  speck(big, 0.018, 0.012, 0.016, 0.006);
  speck(big, -0.016, -0.008, 0.012, 0.005);
  speck(mid, 0.01, -0.012, 0.011, 0.0045);
  return parts;
}

export const mushroomV2 = defineModel({
  id: "mushroom-v2",
  batchId: "fable",
  name: "Mushroom (v2)",
  tags: ["scenery","mushroom","low-poly","age-of-mcp","fable"],
  summary: "Low-poly mushroom.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.06,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => mushroomParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => mushroomParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => mushroomParts(2) },
  ],
  animations: [],
});
